/**
 * SigV4-signed fetch helper. Used to call IAM-authed API Gateway routes
 * (the `/auth/username-check` + `/auth/email-check` availability checks)
 * or any AWS endpoint expecting SigV4 signatures, from the browser.
 *
 * Reads short-lived AWS credentials from Cognito Identity Pool via
 * Amplify's `fetchAuthSession()`. Guest visitors get the unauth role's
 * credentials; signed-in users get the auth role's. Both roles must be
 * granted `execute-api:Invoke` on the check methods by the infra stack —
 * see `infra/.../identity-stack.ts`. (Pre-2026-05-30 these were Lambda
 * Function URLs signed against the `lambda` service; they now sit behind
 * API Gateway, so the signing service is `execute-api`.)
 *
 * The reCAPTCHA layer planned for a future iteration would attach a
 * token header here just before signing (so SigV4 covers it).
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { authConfig } from '../config/auth-config.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

const SERVICE = 'execute-api';

interface SignedFetchOpts {
  readonly method?: 'GET' | 'POST';
  readonly body?: string;
  readonly headers?: Record<string, string>;
  readonly signal?: AbortSignal;
}

/**
 * Fetch `url` with a SigV4 signature over the request (including any
 * query string already present). Throws if credentials are unavailable.
 *
 * The returned `Response` is the raw fetch result — callers inspect
 * `.status` / `.json()` like any other fetch. Body is not consumed
 * here.
 */
export async function signedFetch(
  url: string,
  opts: SignedFetchOpts = {},
): Promise<Response> {
  const session = await fetchAuthSession();
  const credentials = session.credentials;
  if (!credentials) {
    // No guest credentials available — Amplify probably wasn't given
    // `identityPoolId` + `allowGuestAccess`, OR the Identity Pool
    // doesn't allow unauthenticated identities.
    throw new Error('signedFetch: no AWS credentials available');
  }

  const parsed = new URL(url);
  const query: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    query[key] = value;
  });

  const request = new HttpRequest({
    method: opts.method ?? 'GET',
    protocol: parsed.protocol,
    hostname: parsed.hostname,
    port: parsed.port ? Number(parsed.port) : undefined,
    path: parsed.pathname,
    query,
    headers: {
      host: parsed.host,
      ...(opts.headers ?? {}),
    },
    body: opts.body,
  });

  const signer = new SignatureV4({
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    service: SERVICE,
    region: authConfig.region,
    sha256: Sha256,
  });
  const signed = await signer.sign(request);

  // Drop the `host` header — fetch sets it automatically and rejects
  // explicit assignment in browsers.
  const headersOut: Record<string, string> = {};
  for (const [k, v] of Object.entries(signed.headers)) {
    if (k.toLowerCase() === 'host') {
      continue;
    }
    if (typeof v === 'string') {
      headersOut[k] = v;
    }
  }

  // Send to the ORIGINAL URL. The signer canonicalised over the query
  // map derived from this same URL, so the request the browser sends
  // matches the signature. Reassembling the URL via encodeURIComponent
  // can desync (e.g., `+` vs `%20`), which Lambda rejects as 403.
  return fetch(url, {
    method: signed.method,
    headers: headersOut,
    body: signed.body as BodyInit | undefined,
    // Default the caller's signal rather than leaving it undefined: an
    // unsignalled fetch waits on a silent peer forever, which surfaces as a
    // spinner that never resolves and never errors. A caller with its own
    // abort (a stream watchdog, a cancel button) still wins.
    signal: opts.signal ?? requestTimeout(),
  });
}
